import { useMemo, useState } from 'react';
import * as NextRouter from 'next/router';
import { ALL_ARTICLES_LABEL, ALL_STUDIES_LABEL, FilterChipsProps } from './FilterChips.types';
import { buildChipLabels, filterItemsByChip } from './FilterChips.utils';
import { StChipButton, StChipScrollContainer } from './FilterChips.styles';

function FilterChips<T>({
  items,
  onFilterChange,
  getConditionLabel,
  getNavigateHref,
  allLabel = ALL_STUDIES_LABEL,
  align = 'center',
  onNavigate,
}: FilterChipsProps<T>): JSX.Element {
  const [selectedChip, setSelectedChip] = useState(allLabel);
  const chipLabels = useMemo(
    () => buildChipLabels(items, getConditionLabel, allLabel),
    [items, getConditionLabel, allLabel]
  );
  const route = NextRouter.useRouter();

  const handleChipClick = (chip: string) => {
    const filtered = filterItemsByChip(items, chip, getConditionLabel, allLabel);

    if (chip !== allLabel && filtered.length === 1) {
      const href = getNavigateHref(filtered[0]);
      if (href) {
        if (onNavigate) {
          onNavigate(href);
        } else {
          route.push(href);
        }
        return;
      }
    }

    setSelectedChip(chip);
    onFilterChange(filtered);
  };

  return (
    <StChipScrollContainer
      role="tablist"
      id="filter-chips-container"
      aria-label={
        allLabel === ALL_ARTICLES_LABEL
          ? 'Filter articles by condition'
          : 'Filter studies by condition'
      }
      align={align}
    >
      {chipLabels.map((label) => {
        const isActive = selectedChip === label;
        return (
          <StChipButton
            key={label}
            type="button"
            role="tab"
            active={isActive}
            aria-pressed={isActive}
            aria-selected={isActive}
            onClick={() => handleChipClick(label)}
          >
            {label}
          </StChipButton>
        );
      })}
    </StChipScrollContainer>
  );
}

export default FilterChips;
